import { IConstraintOption } from '@/interfaces/portfolioOptimization.interface'
import {
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  styled,
  SxProps,
  Theme,
} from '@mui/material'
import React, { useCallback, useMemo } from 'react'

interface ListOption {
  id: string
  description: string
}

interface ConfigurableListProps {
  options: ListOption[] | IConstraintOption[]
  onSelect: (option: any) => void
  option?: ListOption | IConstraintOption
  searchTerm?: string
  loading?: boolean
  displayId?: boolean
  styles?: SxProps<Theme>
}

const ListOptionStyled = styled(ListItem)(({ theme }) => ({
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: theme.palette.action.hover,
  },
  '&.selected': {
    fontWeight: 700,
    backgroundColor: theme.palette.action.selected,
  },
}))

export const ConfigurableList: React.FC<ConfigurableListProps> = React.memo(
  ({
    options,
    onSelect,
    option,
    searchTerm,
    loading = false,
    displayId = false,
    styles,
  }: ConfigurableListProps) => {
    const filteredOptions = useMemo(() => {
      if (!options) return []
      return (options as any[])?.filter(
        (opt: ListOption | IConstraintOption) => {
          const textToSearch = (opt as ListOption).description
          return (
            !searchTerm ||
            textToSearch?.toLowerCase().includes(searchTerm?.toLowerCase())
          )
        },
      )
    }, [options, searchTerm])

    const handleSelect = useCallback(
      (opt: ListOption | IConstraintOption) => {
        onSelect(opt)
      },
      [onSelect],
    )

    return (
      <List
        sx={{ ...styles, overflow: 'auto', height: 'calc(100% - 55px)' }}
        disablePadding
        dense={true}
      >
        {loading ? (
          <LinearProgress />
        ) : (
          filteredOptions.map((opt) => (
            <ListOptionStyled
              key={opt.id}
              className={opt.id === option?.id ? 'selected' : ''}
              onClick={() => handleSelect(opt)}
            >
              <ListItemText primary={opt.description} />
              {displayId && (
                <ListItemText sx={{ textAlign: 'right' }} primary={opt.id} />
              )}
            </ListOptionStyled>
          ))
        )}
      </List>
    )
  },
)

ConfigurableList.displayName = 'ConfigurableList'
